import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Cookie, Settings, X, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from '@/contexts/TranslationContext';

interface CookiePreferences {
  necessary: boolean;
  performance: boolean;
  functionality: boolean;
  marketing: boolean;
}

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, cannot be disabled
    performance: false,
    functionality: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (!cookieConsent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(cookieConsent);
        setPreferences(savedPreferences);
      } catch (error) {
        // If parsing fails, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);
    
    // Implement actual cookie management logic here
    if (prefs.performance) {
      // Enable Google Analytics, etc.
      console.log('Performance cookies enabled');
    }
    if (prefs.functionality) {
      // Enable functionality cookies
      console.log('Functionality cookies enabled');
    }
    if (prefs.marketing) {
      // Enable marketing cookies
      console.log('Marketing cookies enabled');
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      performance: true,
      functionality: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      performance: false,
      functionality: false,
      marketing: false
    };
    savePreferences(necessaryOnly);
  };

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // Cannot change necessary cookies
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 md:items-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Banner */}
      <Card className="relative w-full max-w-4xl glass border-white/20 shadow-2xl">
        <CardContent className="p-6">
          {!showPreferences ? (
            // Main Banner
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/20 text-primary">
                  <Cookie className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Utilizziamo i Cookie per Migliorare la Tua Esperienza
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    AstroPet utilizza cookie essenziali per il funzionamento del sito e cookie opzionali 
                    per analytics, personalizzazione e marketing. Puoi scegliere quali accettare.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/60 hover:text-white"
                  onClick={() => setShowBanner(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <Button
                  onClick={acceptAll}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold"
                >
                  Accetta Tutti i Cookie
                </Button>
                <Button
                  onClick={acceptNecessaryOnly}
                  variant="outline"
                  className="glass border-white/20 text-white hover:bg-white/10"
                >
                  Solo Cookie Necessari
                </Button>
                <Button
                  onClick={() => setShowPreferences(true)}
                  variant="outline"
                  className="glass border-white/20 text-white hover:bg-white/10"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Personalizza
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 text-sm text-white/60">
                <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                  <span className="flex items-center gap-1">
                    Privacy Policy <ExternalLink className="w-3 h-3" />
                  </span>
                </Link>
                <span>•</span>
                <Link to="/cookie-policy" className="hover:text-primary transition-colors">
                  <span className="flex items-center gap-1">
                    Cookie Policy <ExternalLink className="w-3 h-3" />
                  </span>
                </Link>
                <span>•</span>
                <Link to="/terms-of-service" className="hover:text-primary transition-colors">
                  <span className="flex items-center gap-1">
                    Termini di Servizio <ExternalLink className="w-3 h-3" />
                  </span>
                </Link>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Preferenze Cookie</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/60 hover:text-white"
                  onClick={() => setShowPreferences(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-white">Cookie Necessari</h4>
                      <Badge variant="outline" className="text-xs border-primary text-primary">
                        Richiesti
                      </Badge>
                    </div>
                    <p className="text-sm text-white/70">
                      Essenziali per il funzionamento del sito (autenticazione, sicurezza)
                    </p>
                  </div>
                  <Switch
                    checked={preferences.necessary}
                    disabled={true}
                    className="opacity-50"
                  />
                </div>

                {/* Performance Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Cookie di Performance</h4>
                    <p className="text-sm text-white/70">
                      Analytics e misurazione per migliorare l'esperienza (Google Analytics)
                    </p>
                  </div>
                  <Switch
                    checked={preferences.performance}
                    onCheckedChange={(checked) => handlePreferenceChange('performance', checked)}
                  />
                </div>

                {/* Functionality Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Cookie di Funzionalità</h4>
                    <p className="text-sm text-white/70">
                      Ricordano le tue preferenze (tema, lingua, impostazioni)
                    </p>
                  </div>
                  <Switch
                    checked={preferences.functionality}
                    onCheckedChange={(checked) => handlePreferenceChange('functionality', checked)}
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Cookie di Marketing</h4>
                    <p className="text-sm text-white/70">
                      Pubblicità personalizzata e remarketing (Facebook Pixel, Google Ads)
                    </p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => handlePreferenceChange('marketing', checked)}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <Button
                  onClick={saveCustomPreferences}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold"
                >
                  {t('cookie.savePreferences')}
                </Button>
                <Button
                  onClick={() => setShowPreferences(false)}
                  variant="outline"
                  className="glass border-white/20 text-white hover:bg-white/10"
                >
                  {t('button.cancel')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CookieBanner;